import {
  Card,
  Suit,
  Rank,
  GameState,
  Player,
  PenaltyType,
  GameType,
  OkeyTile,
  OkeyColor,
} from "../types";
import { OkeyValidator } from "./OkeyValidator";
import { nanoid } from "nanoid";

export const SUITS: Suit[] = ["spades", "hearts", "diamonds", "clubs"];
export const RANKS: Rank[] = [
  "A",
  "2",
  "3",
  "4",
  "5",
  "6",
  "7",
  "8",
  "9",
  "10",
  "J",
  "Q",
  "K",
];
export const OKEY_COLORS: OkeyColor[] = ["red", "black", "blue", "yellow"];

export class DeckManager {
  static seededRandom(seed: number) {
    return function () {
      seed |= 0;
      seed = (seed + 0x6d2b79f5) | 0;
      let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
      t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
  }

  static createOkeyDeck(seed?: number | string): OkeyTile[] {
    const deck: OkeyTile[] = [];
    let idCounter = 1;
    for (let set = 0; set < 2; set++) {
      OKEY_COLORS.forEach((color) => {
        for (let value = 1; value <= 13; value++) {
          deck.push({
            id: `okey-${color}-${value}-${idCounter++}`,
            color,
            value,
          });
        }
      });
    }
    deck.push({ id: `okey-fake-1`, color: "fake", value: 0 });
    deck.push({ id: `okey-fake-2`, color: "fake", value: 0 });
    return this.shuffle(deck, seed);
  }

  static async computeHash(data: string): Promise<string> {
    const msgBuffer = new TextEncoder().encode(data);
    const hashBuffer = await crypto.subtle.digest("SHA-256", msgBuffer);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");
  }

  static sortHand(hand: Card[]): Card[] {
    const suitOrder: Record<Suit, number> = {
      spades: 3,
      hearts: 2,
      clubs: 1,
      diamonds: 0,
    };
    const rankOrder: Record<Rank, number> = {
      A: 14,
      K: 13,
      Q: 12,
      J: 11,
      "10": 10,
      "9": 9,
      "8": 8,
      "7": 7,
      "6": 6,
      "5": 5,
      "4": 4,
      "3": 3,
      "2": 2,
      JKR: 15,
    };

    return [...hand].sort((a, b) => {
      if (a.suit && b.suit && suitOrder[a.suit] !== suitOrder[b.suit]) {
        return suitOrder[b.suit] - suitOrder[a.suit];
      }
      return rankOrder[b.rank] - rankOrder[a.rank];
    });
  }

  static createDeck(
    skinId: number = 1,
    includeJokers: boolean = false,
    seed?: number,
  ): Card[] {
    const deck: Card[] = [];
    SUITS.forEach((suit) => {
      RANKS.forEach((rank) => {
        deck.push({
          id: `${rank}-${suit}`, // Use deterministic IDs when using seed for anti-cheat
          suit,
          rank,
          isFaceUp: false,
          skinId: skinId,
        });
      });
    });

    if (includeJokers) {
      // Add 2 Jokers
      deck.push({
        id: `JKR-spades`,
        suit: "spades", // Black Joker
        rank: "JKR",
        isFaceUp: false,
        skinId: skinId,
      });
      deck.push({
        id: `JKR-hearts`,
        suit: "hearts", // Red Joker
        rank: "JKR",
        isFaceUp: false,
        skinId: skinId,
      });
    }
    return this.shuffle(deck, seed);
  }

  static shuffle<T>(deck: T[], seed?: number | string): T[] {
    const newDeck = [...deck];
    const numSeed =
      seed !== undefined
        ? typeof seed === "string"
          ? seed.split("").reduce((acc, char) => (acc * 31 + char.charCodeAt(0)) | 0, 0)
          : seed
        : undefined;
    const rng = numSeed !== undefined ? this.seededRandom(numSeed) : Math.random;
    for (let i = newDeck.length - 1; i > 0; i--) {
      const j = Math.floor(rng() * (i + 1));
      [newDeck[i], newDeck[j]] = [newDeck[j], newDeck[i]];
    }
    return newDeck;
  }
}

export interface PistiScoreBreakdown {
  aces: number;
  jacks: number;
  jokers: number;
  clubs2: boolean;
  diamonds10: boolean;
  pistiScore: number;
  cardsMajority: boolean;
  totalScore: number;
}

export class PistiEngine {
  static calculateScoreDetails(
    cards: Card[],
    pistiCount: number,
    isMajorityWinner: boolean = false,
  ): PistiScoreBreakdown {
    const details: PistiScoreBreakdown = {
      aces: 0,
      jacks: 0,
      jokers: 0,
      clubs2: false,
      diamonds10: false,
      pistiScore: pistiCount * 10,
      cardsMajority: isMajorityWinner,
      totalScore: 0,
    };

    cards.forEach((card) => {
      if (card.rank === "A") details.aces += 1;
      if (card.rank === "J") details.jacks += 1;
      if (card.rank === "JKR") details.jokers += 1;
      if (card.suit === "clubs" && card.rank === "2") details.clubs2 = true;
      if (card.suit === "diamonds" && card.rank === "10")
        details.diamonds10 = true;
    });

    details.totalScore =
      details.aces * 1 +
      details.jacks * 1 +
      details.jokers * 5 +
      (details.clubs2 ? 2 : 0) +
      (details.diamonds10 ? 3 : 0) +
      details.pistiScore +
      (details.cardsMajority ? 3 : 0);

    return details;
  }

  static calculateScore(cards: Card[], pistiCount: number, isMajorityWinner: boolean = false): number {
    return this.calculateScoreDetails(cards, pistiCount, isMajorityWinner).totalScore;
  }

  static checkPisti(playedCard: Card, middlePile: Card[]): boolean {
    if (middlePile.length === 1) {
      const targetCard = middlePile[0];
      return playedCard.rank === targetCard.rank;
    }
    return false;
  }

  static canCapture(playedCard: Card, middlePile: Card[]): boolean {
    if (middlePile.length === 0) return false;
    const topCard = middlePile[middlePile.length - 1];
    return (
      playedCard.rank === topCard.rank ||
      playedCard.rank === "J" ||
      playedCard.rank === "JKR"
    );
  }
}

export class BatakEngine {
  static getValidCards(
    hand: Card[],
    currentTrick: { card: Card; playerId: string }[],
    trumpSuit: Suit | null,
    isTrumpBroken: boolean = false,
  ): Card[] {
    if (currentTrick.length === 0) {
      if (trumpSuit && !isTrumpBroken) {
        const nonTrumps = hand.filter((c) => c.suit !== trumpSuit);
        if (nonTrumps.length > 0) return nonTrumps;
      }
      return hand;
    }
    const ledSuit = currentTrick[0].card.suit;
    const cardsOfSuit = hand.filter((c) => c.suit === ledSuit);
    if (cardsOfSuit.length > 0) {
      // Must follow suit! If player can beat the highest card of the led suit, they must.
      const currentWinnerId = this.determineWinner(
        currentTrick,
        ledSuit,
        trumpSuit,
      );
      const isTrumped = currentTrick.some(
        (t) => t.card.suit === trumpSuit && trumpSuit !== null,
      );
      const winningCard = currentTrick.find(
        (t) => t.playerId === currentWinnerId,
      )?.card;
      if (!isTrumped) {
        if (winningCard && winningCard.suit === ledSuit) {
          const winningValue = this.getCardValue(winningCard, null);
          const capableToBeat = cardsOfSuit.filter(
            (c) => this.getCardValue(c, null) > winningValue,
          );
          if (capableToBeat.length > 0) return capableToBeat;
        }
      } else if (ledSuit === trumpSuit) {
        if (winningCard && winningCard.suit === trumpSuit) {
          const winningValue = this.getCardValue(winningCard, trumpSuit);
          const capableToBeat = cardsOfSuit.filter(
            (c) => this.getCardValue(c, trumpSuit) > winningValue,
          );
          if (capableToBeat.length > 0) return capableToBeat;
        }
      }
      return cardsOfSuit;
    }

    if (trumpSuit) {
      const trumps = hand.filter((c) => c.suit === trumpSuit);
      if (trumps.length > 0) {
        const trumpsInTrick = currentTrick.filter(
          (t) => t.card.suit === trumpSuit,
        );
        if (trumpsInTrick.length > 0) {
          const highestTrumpInTrick = Math.max(
            ...trumpsInTrick.map((t) => this.getCardValue(t.card, trumpSuit)),
          );
          const capableToBeat = trumps.filter(
            (c) => this.getCardValue(c, trumpSuit) > highestTrumpInTrick,
          );
          if (capableToBeat.length > 0) return capableToBeat;
          // Yerdeki kozu büyütemiyorsa serbest: yan kaçabilir veya isterse küçük koz atabilir
          return hand;
        }
        return trumps;
      }
    }
    return hand;
  }

  static getCardValue(card: Card, trumpSuit: Suit | null): number {
    if (card.rank === "JKR") {
      // Black Joker (spades) > Red Joker (hearts) > any other card
      return card.suit === "spades" ? 1000 : 999;
    }

    const values: Record<Rank, number> = {
      "2": 2,
      "3": 3,
      "4": 4,
      "5": 5,
      "6": 6,
      "7": 7,
      "8": 8,
      "9": 9,
      "10": 10,
      J: 11,
      Q: 12,
      K: 13,
      A: 14,
      JKR: 1000,
    };

    let value = values[card.rank];
    if (card.suit === trumpSuit) value += 100;
    return value;
  }

  static determineWinner(
    playedCards: { card: Card; playerId: string }[],
    ledSuit: Suit,
    trumpSuit: Suit | null,
  ): string {
    let bestMove = playedCards[0];
    let bestValue = this.getCardValue(bestMove.card, trumpSuit);

    for (let i = 1; i < playedCards.length; i++) {
      const current = playedCards[i];
      const currentValue = this.getCardValue(current.card, trumpSuit);

      // Joker beats everything
      if (current.card.rank === "JKR") {
        if (bestMove.card.rank !== "JKR" || currentValue > bestValue) {
          bestMove = current;
          bestValue = currentValue;
        }
        continue;
      }

      if (bestMove.card.rank === "JKR") continue;

      if (current.card.suit === ledSuit || current.card.suit === trumpSuit) {
        if (currentValue > bestValue) {
          bestMove = current;
          bestValue = currentValue;
        }
      }
    }
    return bestMove.playerId;
  }

  static calculateScore(
    bid: number,
    tricksWon: number,
    isBidWinner: boolean,
    variant: "ihaleli" | "kozmaca" | "gomulu" | "esli" = "ihaleli",
  ): number {
    if (variant === "ihaleli" || variant === "gomulu" || variant === "esli") {
      if (isBidWinner) {
        if (tricksWon >= bid) {
          // Points: (Bid * 10) + (Extra Tricks)
          return bid * 10 + (tricksWon - bid);
        } else {
          // Penalty: -(Bid * 10)
          return -(bid * 10);
        }
      } else {
        // Players who didn't win the bid
        if (tricksWon >= 1) {
          // Points: tricks won * 10
          return tricksWon * 10;
        } else {
          // "Batak" penalty for zero tricks
          return variant === "esli" ? 0 : -50;
        }
      }
    } else {
      // Koz Maça: everyone is evaluated against their bid
      if (tricksWon >= bid) {
        return bid * 10 + (tricksWon - bid);
      } else {
        return -(bid * 10);
      }
    }
  }
}

export class KingEngine {
  static getValidCards(
    hand: Card[],
    currentTrick: { card: Card; playerId: string }[],
    trumpSuit: Suit | null,
    activePenalty: PenaltyType,
    isTrumpBroken: boolean = false,
  ): Card[] {
    if (currentTrick.length === 0) {
      if (activePenalty === "hearts" || activePenalty === "king-hearts") {
        if (!isTrumpBroken) {
          const nonHearts = hand.filter((c) => c.suit !== "hearts");
          if (nonHearts.length > 0) return nonHearts;
        }
      } else if (trumpSuit) {
        if (!isTrumpBroken) {
          const nonTrumps = hand.filter((c) => c.suit !== trumpSuit);
          if (nonTrumps.length > 0) return nonTrumps;
        }
      }
      return hand;
    }
    const ledSuit = currentTrick[0].card.suit;
    const cardsOfSuit = hand.filter((c) => c.suit === ledSuit);
    if (cardsOfSuit.length > 0) {
      if (trumpSuit) {
        const currentWinnerId = this.determineWinner(
          currentTrick,
          ledSuit,
          trumpSuit,
        );
        const isTrumped = currentTrick.some((t) => t.card.suit === trumpSuit);
        const winningCard = currentTrick.find(
          (t) => t.playerId === currentWinnerId,
        )?.card;
        if (!isTrumped) {
          if (winningCard && winningCard.suit === ledSuit) {
            const winningValue = this.getCardValue(winningCard);
            const capableToBeat = cardsOfSuit.filter(
              (c) => this.getCardValue(c) > winningValue,
            );
            if (capableToBeat.length > 0) return capableToBeat;
          }
        } else if (ledSuit === trumpSuit) {
          if (winningCard && winningCard.suit === trumpSuit) {
            const winningValue = this.getCardValue(winningCard);
            const capableToBeat = cardsOfSuit.filter(
              (c) => this.getCardValue(c) > winningValue,
            );
            if (capableToBeat.length > 0) return capableToBeat;
          }
        }
      }
      return cardsOfSuit;
    }

    if (trumpSuit) {
      const trumps = hand.filter((c) => c.suit === trumpSuit);
      if (trumps.length > 0) {
        const trumpsInTrick = currentTrick.filter(
          (t) => t.card.suit === trumpSuit,
        );
        if (trumpsInTrick.length > 0) {
          const highestTrumpInTrick = Math.max(
            ...trumpsInTrick.map((t) => this.getCardValue(t.card)),
          );
          const capableToBeat = trumps.filter(
            (c) => this.getCardValue(c) > highestTrumpInTrick,
          );
          if (capableToBeat.length > 0) return capableToBeat;
          // Yerdeki kozu büyütemiyorsa serbest: yan kaçabilir veya isterse küçük koz atabilir
          return hand;
        }
        return trumps;
      }
    } else {
      // Penalty games: if they don't have the led suit, they can play anything.
      return hand;
    }
    return hand;
  }

  static getCardValue(card: Card): number {
    if (card.rank === "JKR") {
      return card.suit === "spades" ? 1000 : 999;
    }
    const values: Record<Rank, number> = {
      "2": 2,
      "3": 3,
      "4": 4,
      "5": 5,
      "6": 6,
      "7": 7,
      "8": 8,
      "9": 9,
      "10": 10,
      J: 11,
      Q: 12,
      K: 13,
      A: 14,
      JKR: 1000, // JKR handled above, but for completeness
    };
    return values[card.rank];
  }

  static determineWinner(
    trick: { card: Card; playerId: string }[],
    ledSuit: Suit,
    trumpSuit: Suit | null,
  ): string {
    let bestMove = trick[0];

    for (let i = 1; i < trick.length; i++) {
      const current = trick[i];
      const best = bestMove.card;
      const curr = current.card;

      if (curr.rank === "JKR") {
        if (
          best.rank !== "JKR" ||
          this.getCardValue(curr) > this.getCardValue(best)
        ) {
          bestMove = current;
        }
        continue;
      }

      if (best.rank === "JKR") continue;

      if (trumpSuit) {
        if (curr.suit === trumpSuit && best.suit !== trumpSuit) {
          bestMove = current;
        } else if (curr.suit === trumpSuit && best.suit === trumpSuit) {
          if (this.getCardValue(curr) > this.getCardValue(best)) {
            bestMove = current;
          }
        } else if (curr.suit === ledSuit && best.suit !== trumpSuit) {
          if (this.getCardValue(curr) > this.getCardValue(best)) {
            bestMove = current;
          }
        }
      } else {
        if (curr.suit === ledSuit) {
          if (this.getCardValue(curr) > this.getCardValue(best)) {
            bestMove = current;
          }
        }
      }
    }
    return bestMove.playerId;
  }

  static calculatePenalty(
    cards: Card[],
    penaltyType: PenaltyType,
    isLastTrick: boolean,
    isSecondToLastTrick: boolean = false,
  ): number {
    let penalty = 0;
    if (penaltyType === "tricks") return -50;

    cards.forEach((card) => {
      if (penaltyType === "hearts" && card.suit === "hearts") penalty -= 30;
      if (penaltyType === "girls" && card.rank === "Q") penalty -= 100;
      if (penaltyType === "boys" && (card.rank === "J" || card.rank === "K"))
        penalty -= 60;
      if (
        penaltyType === "king-hearts" &&
        card.rank === "K" &&
        card.suit === "hearts"
      )
        penalty -= 320;
    });

    if (penaltyType === "last-two" && (isLastTrick || isSecondToLastTrick))
      penalty -= 180;

    return penalty;
  }

  static calculateTrumpScore(tricksWon: number): number {
    return tricksWon * 50;
  }
}

export class OkeyEngine {
  static distributeTiles(state: GameState): GameState {
    const newState = { ...state };
    const deck = DeckManager.createOkeyDeck(newState.seed);

    let indicator = deck.pop()!;
    while (indicator.color === "fake") {
      deck.unshift(indicator);
      indicator = deck.pop()!;
    }
    newState.okeyIndicator = indicator;
    newState.okeyDeck = deck;

    const firstPlayerIndex = (newState.dealerIndex + 1) % newState.players.length;

    newState.players = newState.players.map((p, index) => {
      const tileCount = index === firstPlayerIndex ? 15 : 14;
      const rack = newState.okeyDeck!.splice(0, tileCount);
      return {
        ...p,
        okeyState: { rack, discards: [], score: p.okeyState?.score || 0 },
      };
    });

    newState.turnIndex = firstPlayerIndex;
    newState.status = "dealing";
    newState.okeyDoublers = [];
    newState.okeyIndicatorShownBy = [];
    newState.okeyDiscardPiles = {};
    newState.players.forEach((p) => {
      newState.okeyDiscardPiles![p.id] = [];
    });

    return newState;
  }

  static discardTile(state: GameState, playerId: string, tileId: string): GameState {
    const newState = { ...state };
    const playerIndex = newState.players.findIndex(p => p.id === playerId);
    if (playerIndex === -1) return newState;
    
    const player = newState.players[playerIndex];
    if (!player.okeyState) return newState;
    if (newState.turnIndex !== playerIndex) return newState;
    if (player.okeyState.rack.length < 15) return newState;
    
    const rackIndex = player.okeyState.rack.findIndex(t => t.id === tileId);
    if (rackIndex === -1) return newState;
    
    // Deep copy the players array and the specific player to avoid mutating the original state
    newState.players = [...newState.players];
    newState.players[playerIndex] = {
      ...player,
      okeyState: {
        ...player.okeyState,
        rack: [...player.okeyState.rack]
      }
    };
    
    const tileToDiscard = newState.players[playerIndex].okeyState!.rack.splice(rackIndex, 1)[0];
    
    // Check if remaining 14 tiles form a winning hand
    const checkResult = OkeyValidator.checkWin(
      newState.players[playerIndex].okeyState!.rack,
      newState.okeyIndicator!
    );

    const isDoubler = newState.okeyDoublers?.includes(playerId) ?? false;
    
    if (checkResult.isWin) {
      if (isDoubler && !checkResult.isDouble) {
        // Player declared double, must win by double
        checkResult.isWin = false;
      }
    }

    if (checkResult.isWin) {
      newState.status = "finished";
      newState.lastWinnerId = playerId;
      
      const isOkeyFinish = OkeyValidator.isOkey(tileToDiscard, OkeyValidator.getOkeyTileDefinition(newState.okeyIndicator!));
      
      let points = 2;
      if (checkResult.isDouble || isDoubler) points *= 2;
      if (isOkeyFinish) points *= 2;
      
      newState.players[playerIndex].score += points;
      newState.players[playerIndex].okeyState!.score += points;
    }
    
    newState.okeyDiscardPiles = { ...newState.okeyDiscardPiles };
    newState.okeyDiscardPiles[playerId] = [...(newState.okeyDiscardPiles[playerId] || []), tileToDiscard];
    
    if (newState.status !== "finished") {
      newState.turnIndex = (newState.turnIndex + 1) % newState.players.length;
    }
    newState.lastAction = { type: 'okey_discard', playerId, okeyTile: tileToDiscard, timestamp: Date.now() };
    
    return newState;
  }
  
  static drawTile(state: GameState, playerId: string, source: "deck" | "discard"): GameState {
    const newState = { ...state };
    const playerIndex = newState.players.findIndex(p => p.id === playerId);
    if (playerIndex === -1) return newState;
    
    const player = newState.players[playerIndex];
    if (!player.okeyState) return newState;
    if (newState.turnIndex !== playerIndex) return newState;
    if (player.okeyState.rack.length >= 15) return newState;
    
    newState.players = [...newState.players];
    newState.players[playerIndex] = {
      ...player,
      okeyState: {
        ...player.okeyState,
        rack: [...player.okeyState.rack]
      }
    };
    
    let drawnTile;
    if (source === "deck") {
       if (newState.okeyDeck && newState.okeyDeck.length > 0) {
           newState.okeyDeck = [...newState.okeyDeck];
           drawnTile = newState.okeyDeck.pop()!;
           newState.players[playerIndex].okeyState!.rack.push(drawnTile);
       } else {
           // Deck is empty - game ends in a tie (no winner)
           newState.status = "finished";
           newState.lastWinnerId = null;
           newState.lastAction = { type: 'okey_draw', playerId, okeyTile: undefined as any, timestamp: Date.now() };
           return newState;
       }
    } else {
       const prevPlayerIndex = (playerIndex - 1 + newState.players.length) % newState.players.length;
       const prevPlayerId = newState.players[prevPlayerIndex].id;
       const discardPile = newState.okeyDiscardPiles![prevPlayerId];
       if (discardPile && discardPile.length > 0) {
           newState.okeyDiscardPiles = { ...newState.okeyDiscardPiles };
           newState.okeyDiscardPiles[prevPlayerId] = [...discardPile];
           drawnTile = newState.okeyDiscardPiles[prevPlayerId].pop()!;
           newState.players[playerIndex].okeyState!.rack.push(drawnTile);
       }
    }
    
    if (drawnTile) {
      newState.lastAction = { type: 'okey_draw', playerId, okeyTile: drawnTile, timestamp: Date.now() };
    }
    
    return newState;
  }

  static toggleDouble(state: GameState, playerId: string): GameState {
    const newState = { ...state };
    newState.okeyDoublers = [...(newState.okeyDoublers || [])];
    
    const idx = newState.okeyDoublers.indexOf(playerId);
    if (idx !== -1) {
      newState.okeyDoublers.splice(idx, 1);
    } else {
      newState.okeyDoublers.push(playerId);
    }
    
    return newState;
  }

  static showIndicator(state: GameState, playerId: string): GameState {
    const newState = { ...state };
    newState.okeyIndicatorShownBy = [...(newState.okeyIndicatorShownBy || [])];
    
    if (newState.okeyIndicatorShownBy.includes(playerId)) return state;
    
    const player = newState.players.find(p => p.id === playerId);
    if (!player || !player.okeyState) return state;

    // Must be on turn 1 before the player makes any discard
    const playerDiscards = newState.okeyDiscardPiles?.[playerId] || [];
    if (playerDiscards.length > 0) return state;
    
    const indicatorDef = OkeyValidator.getOkeyTileDefinition(newState.okeyIndicator!);
    if (!indicatorDef) return state;
    
    // Check if player actually has the exact indicator tile (same color and value as okeyIndicator)
    const hasIndicator = player.okeyState.rack.some(
      t => t.color === newState.okeyIndicator!.color && t.value === newState.okeyIndicator!.value
    );
    
    if (hasIndicator) {
      newState.okeyIndicatorShownBy.push(playerId);
      newState.players = newState.players.map(p => {
        if (p.id !== playerId) {
          return {
            ...p,
            score: p.score - 1,
            okeyState: { ...p.okeyState!, score: (p.okeyState?.score || 0) - 1 }
          };
        }
        return p;
      });
    }
    
    return newState;
  }

  static replenishBotRacks(state: GameState): GameState {
    if (state.gameType !== "okey") return state;
    const newState = { ...state };

    const isDummyTile = (t: OkeyTile | undefined | null) => {
      if (!t) return true;
      if (t.color === "fake") return false;
      const num = typeof t.value === "number" ? t.value : Number(t.value);
      return !["red", "black", "blue", "yellow"].includes(t.color as string) || isNaN(num) || num < 1 || num > 13;
    };

    const hasBotNeedingReplenish = newState.players.some((p) => {
      if (!p.isBot || !p.okeyState) return false;
      const rack = p.okeyState.rack || [];
      return rack.length === 0 || rack.some(isDummyTile);
    });

    const isDeckDummy = (newState.okeyDeck || []).some(isDummyTile);

    if (!hasBotNeedingReplenish && !isDeckDummy) return newState;

    // Track used tiles across indicators, discards, and known valid racks
    const usedCounts = new Map<string, number>();
    const markUsed = (t: OkeyTile | undefined | null) => {
      if (!t || isDummyTile(t)) return;
      const key = `${t.color}-${t.value}`;
      usedCounts.set(key, (usedCounts.get(key) || 0) + 1);
    };

    if (newState.okeyIndicator) markUsed(newState.okeyIndicator);

    if (newState.okeyDiscardPiles) {
      Object.values(newState.okeyDiscardPiles).flat().forEach(markUsed);
    }

    newState.players.forEach((p) => {
      if (!p.okeyState?.rack) return;
      const isRackDummy = p.okeyState.rack.some(isDummyTile);
      if (!isRackDummy) {
        p.okeyState.rack.forEach(markUsed);
      }
    });

    // Create a pool from standard 106-tile set
    const fullDeck = DeckManager.createOkeyDeck();
    for (let i = fullDeck.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [fullDeck[i], fullDeck[j]] = [fullDeck[j], fullDeck[i]];
    }

    const availablePool: OkeyTile[] = [];
    const poolCounts = new Map<string, number>();

    for (const t of fullDeck) {
      const key = `${t.color}-${t.value}`;
      const used = usedCounts.get(key) || 0;
      const inPool = poolCounts.get(key) || 0;
      if (used + inPool < 2) {
        availablePool.push(t);
        poolCounts.set(key, inPool + 1);
      }
    }

    // Assign to bots needing valid tiles
    newState.players = newState.players.map((p, pIdx) => {
      if (!p.isBot) return p;
      const rack = p.okeyState?.rack || [];
      const needsReplenish = rack.length === 0 || rack.some(isDummyTile);
      if (!needsReplenish) return p;

      // Determine exact valid tile count: in Okey, a rack must have 15 tiles ONLY if this player
      // has drawn on their turn or if it's the opening turn for the first player before discarding.
      // In all other cases, it must be exactly 14 tiles.
      let count = 14;
      if (rack.length === 15) {
        count = 15;
      } else if (rack.length === 14) {
        count = 14;
      } else {
        const isCurrentTurn = pIdx === (newState.turnIndex ?? 0);
        const hasDrawnThisTurn = newState.lastAction?.type === 'okey_draw' && (newState.lastAction.playerId === p.id);
        const isFirstPlayerOpening = (pIdx === (newState.firstPlayerIndex ?? 0)) && (newState.turnIndex === newState.firstPlayerIndex) && (!newState.lastAction || newState.lastAction.type !== 'okey_discard');
        if (isCurrentTurn && (hasDrawnThisTurn || isFirstPlayerOpening)) {
          count = 15;
        } else {
          count = 14;
        }
      }

      const newRack = availablePool.splice(0, count);
      let idCounter = 1;
      while (newRack.length < count) {
        const fallbackColors: OkeyColor[] = ["red", "black", "blue", "yellow"];
        const randColor = fallbackColors[Math.floor(Math.random() * fallbackColors.length)];
        const randVal = Math.floor(Math.random() * 13) + 1;
        newRack.push({
          id: `replenish-extra-${p.id}-${idCounter++}`,
          color: randColor,
          value: randVal,
        });
      }

      return {
        ...p,
        okeyState: {
          rack: newRack,
          discards: p.okeyState?.discards || [],
          score: p.okeyState?.score || p.score || 0,
        },
      };
    });

    if (isDeckDummy || (newState.okeyDeck || []).length === 0) {
      newState.okeyDeck = availablePool;
    }

    return newState;
  }
}

export const INITIAL_GAME_STATE = (
  hostId: string,
  gameType: GameType = "pisti",
  hostName: string = "Host Player",
  avatar?: string,
  maxPlayers?: number,
  targetScore?: number,
  bluffingMode?: boolean,
): GameState => ({
  gameType,
  isTeamGame: false,
  maxPlayers: maxPlayers || (gameType === "pisti" ? 2 : 4),
  targetScore,
  bluffingMode,
  status: "waiting",
  deck: [],
  middlePile: [],
  currentTrick: [],
  players: [
    {
      id: hostId,
      name: hostName,
      avatar,
      isHost: true,
      isBot: false,
      hand: [],
      score: 0,
      collectedCards: [],
      pistiCount: 0,
      remainingPenalties: [
        "tricks",
        "hearts",
        "girls",
        "boys",
        "king-hearts",
        "last-two",
      ],
      remainingTrumps: 2,
      bid: 0,
      ...(gameType === "okey"
        ? { okeyState: { rack: [], discards: [], score: 0 } }
        : {}),
    },
  ],
  turnIndex: 0,
  dealerIndex: 0,
  bidderIndex: 0,
  batakVariant: "ihaleli",
  trumpSuit: null,
  activePenalty: "none",
  lastWinnerId: null,
  history: [],
  faceStyle: "relief",
  ...(gameType === "okey" ? { okeyDeck: [], okeyDiscardPiles: {} } : {}),
});
